import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import clsx from 'clsx'
import LegacySelect from '../Select'

/** Společný vzhled textových polí (výška 44 px = dotyková plocha). */
export const inputBase =
  'block w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition ' +
  'placeholder:text-ink-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25 ' +
  'disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-ink-500 aria-[invalid=true]:border-red-500'

/**
 * Popisek + pole + nápověda/chyba. Nápověda je vidět vždy (žádné schovávání do title).
 * `htmlFor` se doplní sám, pokud je pole jediné dítě s `id` — jinak ho předej.
 */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  className,
  children,
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  htmlFor?: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={clsx('min-w-0', className)}>
      {label && (
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink-800">
          {label}
          {required && <span className="ml-0.5 text-brand-700" aria-hidden>*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="mt-1 text-xs font-medium text-red-700" role="alert">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-ink-500">{hint}</p>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Ikona uvnitř pole vlevo. */
  leading?: ReactNode
  /** Text/ikona uvnitř pole vpravo (jednotka „h", „km"). */
  trailing?: ReactNode
  invalid?: boolean
}

/** Textové pole. `ref` prochází (fokus po výběru z našeptávače). */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leading, trailing, invalid, className, ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      {...props}
      className={clsx(inputBase, 'h-11', leading && 'pl-10', trailing && 'pr-10', className)}
    />
  )
  if (!leading && !trailing) return input
  return (
    <div className="relative">
      {leading && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">{leading}</span>}
      {input}
      {trailing && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-500">{trailing}</span>}
    </div>
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, rows = 3, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={invalid || undefined}
        {...props}
        className={clsx(inputBase, 'min-h-[88px] resize-y py-2.5 leading-relaxed', className)}
      />
    )
  },
)

/**
 * Výběr ze seznamu. Aplikace používá vlastní Select s hledáním a seznamem v portálu
 * (`components/Select.tsx`) — tady je jen ve výchozím vzhledu na plnou šířku a 44 px výšky.
 * API je stejné jako u nativního <select>: `value`, `onChange(e => e.target.value)`, <option> děti.
 */
export function Select(props: React.ComponentProps<typeof LegacySelect>) {
  return <LegacySelect {...props} className={clsx('h-11 w-full', props.className)} />
}

/** Zaškrtávací pole s popiskem; celý řádek je klikací (min. 44 px). */
export function Checkbox({
  label,
  description,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: ReactNode; description?: ReactNode }) {
  const id = useId()
  return (
    <label htmlFor={props.id ?? id} className={clsx('flex min-h-[44px] cursor-pointer items-start gap-3 py-2', props.disabled && 'cursor-not-allowed opacity-60', className)}>
      <input
        id={props.id ?? id}
        type="checkbox"
        {...props}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-brand-600"
      />
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block text-sm font-medium text-ink-800">{label}</span>}
          {description && <span className="mt-0.5 block text-xs text-ink-500">{description}</span>}
        </span>
      )}
    </label>
  )
}

/** Přepínač zap/vyp (role="switch"). Pro okamžitě platná nastavení; ve formuláři s Uložit spíš Checkbox. */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  className,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label?: ReactNode
  description?: ReactNode
  disabled?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx('flex min-h-[44px] items-center gap-3 text-left disabled:cursor-not-allowed disabled:opacity-50', className)}
    >
      <span className={clsx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors', checked ? 'bg-brand-600' : 'bg-slate-300')}>
        <span className={clsx('inline-block h-5 w-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
      </span>
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block text-sm font-medium text-ink-800">{label}</span>}
          {description && <span className="block text-xs text-ink-500">{description}</span>}
        </span>
      )}
    </button>
  )
}

/** Segmentový přepínač (2–4 volby) — např. Celý den / Dopoledne / Odpoledne. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: {
  options: { value: T; label: ReactNode }[]
  value: T
  onChange: (value: T) => void
  className?: string
  ariaLabel?: string
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={clsx('inline-flex w-full rounded-lg bg-slate-100 p-1 sm:w-auto', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'min-h-[40px] flex-1 rounded-md px-3 text-sm font-medium transition sm:flex-none',
            value === o.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600 hover:text-ink-900',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
